/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsDuplicateBuiltIn } from "../funcs/dashboardsDuplicateBuiltIn.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DuplicateBuiltInDashboardRequest,
  DuplicateBuiltInDashboardSecurity,
} from "../models/operations/duplicatebuiltindashboard.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DuplicateBuiltInDashboardMutationVariables = {
  request: DuplicateBuiltInDashboardRequest;
  security?: DuplicateBuiltInDashboardSecurity | undefined;
  options?: RequestOptions;
};

export type DuplicateBuiltInDashboardMutationData = Dashboard;

export type DuplicateBuiltInDashboardMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * duplicateBuiltInDashboard dashboards
 *
 * @remarks
 * Copy a Speakeasy-built dashboard into a new one the caller owns, named "<name> (copy)", with a new saved widget per card, named the same way. The copy is a project dashboard like any other and can be changed; the built-in stays as it is.
 */
export function useDuplicateBuiltInDashboardMutation(
  options?: MutationHookOptions<
    DuplicateBuiltInDashboardMutationData,
    DuplicateBuiltInDashboardMutationError,
    DuplicateBuiltInDashboardMutationVariables
  >,
): UseMutationResult<
  DuplicateBuiltInDashboardMutationData,
  DuplicateBuiltInDashboardMutationError,
  DuplicateBuiltInDashboardMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDuplicateBuiltInDashboardMutation(client, options),
    ...options,
  });
}

export function mutationKeyDuplicateBuiltInDashboard(): MutationKey {
  return ["@gram/client", "dashboards", "duplicateBuiltIn"];
}

export function buildDuplicateBuiltInDashboardMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DuplicateBuiltInDashboardMutationVariables,
  ) => Promise<DuplicateBuiltInDashboardMutationData>;
} {
  return {
    mutationKey: mutationKeyDuplicateBuiltInDashboard(),
    mutationFn: function duplicateBuiltInDashboardMutationFn({
      request,
      security,
      options,
    }): Promise<DuplicateBuiltInDashboardMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsDuplicateBuiltIn(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
